/**
 * FREE-plan support card (Figma Kin Chatbot frame `13398:38664`, PRD "Handoff Requirements,
 * Free Plan"). Free orgs get no live agent: the card points at the support mailbox instead.
 * Rendered for the `email_card` handoff variant and when the accept route answers 403
 * `support_handoff_not_entitled` (PS-1360).
 *
 * Per the frame: the explanatory copy sits above the card as Kin's own message, and the card
 * itself is the envelope heading, the response-time caption, and two actions. Copy Email exists
 * because a mailto: link is useless to anyone whose mail client is not the system default.
 */

import { Box, Button, HStack, Text } from "@chakra-ui/react";
import { useState } from "react";

import {
  ADVISOR_PANEL_BOX_SHADOW,
  ADVISOR_PANEL_NEUTRAL_100,
  ADVISOR_PANEL_ON_PRIMARY, ADVISOR_PANEL_PRIMARY_500,
} from "./advisor-panel-tokens";

export const KINTSUGI_SUPPORT_EMAIL = "support@trykintsugi.com";

/**
 * Confirmed copy, frame `13398:38664` (TDD 4.2), now carried by the **toast** on the persistent
 * control's `not_entitled` path rather than by the card.
 *
 * The frame shows it as Kin's message above the card, but the frame does not model the sentence
 * the model writes on the same turn - which always arrives, and says the same thing. Rendering
 * both gave the customer two Kin bubbles in a row saying one thing twice, where the paid path has
 * one. The card now mirrors the offer card: Kin's own message, then controls.
 */
export const SUPPORT_EMAIL_CARD_BODY =
  "I understand this needs human attention. Data issues require manual review by our support team. You can reach out to us via email and we'll get back to you as soon as possible";
export const SUPPORT_EMAIL_CARD_CTA = "Contact Support";
export const SUPPORT_EMAIL_CARD_CAPTION = "We'll respond within 24 hours";
export const SUPPORT_EMAIL_COPY_LABEL = "Copy Email";
export const SUPPORT_EMAIL_SEND_LABEL = "Send Email";
export const SUPPORT_EMAIL_COPIED_LABEL = "Copied";

export interface SupportEmailCardProps {
  supportEmail?: string;
}

export function SupportEmailCard({
  supportEmail = KINTSUGI_SUPPORT_EMAIL,
}: SupportEmailCardProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(supportEmail);
      setCopied(true);
    } catch {
      // Clipboard access can be denied or unavailable (insecure context, permissions).
      // Send Email still works, and the address is visible in the heading.
      setCopied(false);
    }
  };

  return (
    <Box
      w="100%"
      bg="white"
      borderWidth="1px"
      borderColor={ADVISOR_PANEL_NEUTRAL_100}
      borderRadius="8px"
      boxShadow={ADVISOR_PANEL_BOX_SHADOW}
      px={4}
      pt={3}
      pb={3}
      data-testid="support-email-card"
    >
      <Text fontSize="sm" fontWeight="semibold" color="fg">
        {SUPPORT_EMAIL_CARD_CTA}
      </Text>
      <Text fontSize="xs" color="fg.muted" mb={3}>
        {SUPPORT_EMAIL_CARD_CAPTION}
      </Text>
      <HStack gap={2}>
        <Button size="sm" flex={1} variant="outline" onClick={handleCopy}>
          {copied ? SUPPORT_EMAIL_COPIED_LABEL : SUPPORT_EMAIL_COPY_LABEL}
        </Button>
        <Button
          asChild
          size="sm"
          flex={1}
          bg={ADVISOR_PANEL_PRIMARY_500}
          color={ADVISOR_PANEL_ON_PRIMARY}
          _hover={{ opacity: 0.9 }}
        >
          <a href={`mailto:${supportEmail}`}>{SUPPORT_EMAIL_SEND_LABEL}</a>
        </Button>
      </HStack>
    </Box>
  );
}
